import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Loader2,
  Eye,
  EyeOff,
} from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import logo from '@/assets/logo.png'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string | null
  content: string | null
  author: string
  category: string
  image_url: string | null
  video_url: string | null
  published: boolean
  created_at: string
}

const emptyPost = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  author: 'Equipe FotoLivro',
  category: 'Dicas',
  image_url: '',
  video_url: '',
  published: false,
}

export default function AdminBlog() {
  const { user, isAdmin, loading, signOut } = useAuth()
  const navigate = useNavigate()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [isLoadingPosts, setIsLoadingPosts] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null)
  const [formData, setFormData] = useState(emptyPost)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/admin/login')
    }
  }, [user, isAdmin, loading, navigate])

  useEffect(() => {
    if (isAdmin) {
      fetchPosts()
    }
  }, [isAdmin])

  const fetchPosts = async () => {
    setIsLoadingPosts(true)
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      toast.error('Erro ao carregar posts')
      console.error(error)
    } else {
      setPosts(data || [])
    }
    setIsLoadingPosts(false)
  }

  const handleOpenCreate = () => {
    setEditingPost(null)
    setFormData(emptyPost)
    setIsDialogOpen(true)
  }

  const handleOpenEdit = (post: BlogPost) => {
    setEditingPost(post)
    setFormData({
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt || '',
      content: post.content || '',
      author: post.author,
      category: post.category,
      image_url: post.image_url || '',
      video_url: post.video_url || '',
      published: post.published,
    })
    setIsDialogOpen(true)
  }

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingPost ? prev.slug : generateSlug(title),
    }))
  }

  const handleSave = async () => {
    if (!formData.title || !formData.slug) {
      toast.error('Título e slug são obrigatórios')
      return
    }

    setIsSaving(true)

    if (editingPost) {
      const { error } = await supabase
        .from('blog_posts')
        .update({
          slug: formData.slug,
          title: formData.title,
          excerpt: formData.excerpt || null,
          content: formData.content || null,
          author: formData.author,
          category: formData.category,
          image_url: formData.image_url || null,
          video_url: formData.video_url || null,
          published: formData.published,
        })
        .eq('id', editingPost.id)

      if (error) {
        toast.error('Erro ao atualizar post')
        console.error(error)
      } else {
        toast.success('Post atualizado!')
        setIsDialogOpen(false)
        fetchPosts()
      }
    } else {
      const { error } = await supabase.from('blog_posts').insert({
        slug: formData.slug,
        title: formData.title,
        excerpt: formData.excerpt || null,
        content: formData.content || null,
        author: formData.author,
        category: formData.category,
        image_url: formData.image_url || null,
        video_url: formData.video_url || null,
        published: formData.published,
      })

      if (error) {
        if (error.code === '23505') {
          toast.error('Já existe um post com esse slug')
        } else {
          toast.error('Erro ao criar post')
        }
        console.error(error)
      } else {
        toast.success('Post criado!')
        setIsDialogOpen(false)
        fetchPosts()
      }
    }

    setIsSaving(false)
  }

  const handleDelete = async (post: BlogPost) => {
    if (!confirm(`Tem certeza que deseja excluir "${post.title}"?`)) return

    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', post.id)

    if (error) {
      toast.error('Erro ao excluir post')
      console.error(error)
    } else {
      toast.success('Post excluído!')
      fetchPosts()
    }
  }

  const handleTogglePublished = async (post: BlogPost) => {
    const { error } = await supabase
      .from('blog_posts')
      .update({ published: !post.published })
      .eq('id', post.id)

    if (error) {
      toast.error('Erro ao atualizar status')
    } else {
      fetchPosts()
    }
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/admin/login')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!isAdmin) return null

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logo} alt="FotoLivro" className="h-8" />
            <span className="font-semibold">Painel Admin</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Posts do Blog</CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleOpenCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Post
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingPost ? 'Editar Post' : 'Novo Post'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Título *</Label>
                      <Input
                        value={formData.title}
                        onChange={e => handleTitleChange(e.target.value)}
                        placeholder="Título do post"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slug *</Label>
                      <Input
                        value={formData.slug}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            slug: e.target.value,
                          }))
                        }
                        placeholder="url-do-post"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Autor</Label>
                      <Input
                        value={formData.author}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            author: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoria</Label>
                      <Input
                        value={formData.category}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            category: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Resumo</Label>
                    <Textarea
                      value={formData.excerpt}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          excerpt: e.target.value,
                        }))
                      }
                      placeholder="Breve descrição do post"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Conteúdo (HTML)</Label>
                    <Textarea
                      value={formData.content}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          content: e.target.value,
                        }))
                      }
                      placeholder="<p>Conteúdo do post em HTML...</p>"
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>URL da Imagem</Label>
                      <Input
                        value={formData.image_url}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            image_url: e.target.value,
                          }))
                        }
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL do Vídeo (Vimeo/YouTube)</Label>
                      <Input
                        value={formData.video_url}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            video_url: e.target.value,
                          }))
                        }
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.published}
                      onCheckedChange={checked =>
                        setFormData(prev => ({ ...prev, published: checked }))
                      }
                    />
                    <Label>Publicado</Label>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      {isSaving && (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      )}
                      {editingPost ? 'Atualizar' : 'Criar'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {isLoadingPosts ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : posts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Nenhum post encontrado. Crie o primeiro!
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Autor</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map(post => (
                    <TableRow key={post.id}>
                      <TableCell className="font-medium">
                        {post.title}
                      </TableCell>
                      <TableCell>{post.category}</TableCell>
                      <TableCell>{post.author}</TableCell>
                      <TableCell>
                        {format(new Date(post.created_at), 'dd MMM yyyy', {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleTogglePublished(post)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            post.published
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}
                        >
                          {post.published ? (
                            <Eye className="h-3 w-3" />
                          ) : (
                            <EyeOff className="h-3 w-3" />
                          )}
                          {post.published ? 'Publicado' : 'Rascunho'}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEdit(post)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(post)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
